import type { NavigateOptions } from "@tanstack/react-router";

import type { Icons } from "components/icon/iconTypes";
import type { TagProps } from "components/ui/tag/Tag";

export type CommandGroupId =
  | "recent"
  | "actions"
  | "service_actions"
  | "project_actions"
  | "services"
  | "projects"
  | "service_pages"
  | "project_pages"
  | "account";

// Key order doubles as the display order of groups in the palette. "recent"
// is synthesized by the palette from selection history, never registered.
export const COMMAND_GROUP_TITLES: Record<CommandGroupId, string> = {
  recent: "Recently used",
  actions: "Quick actions",
  service_actions: "Service actions",
  project_actions: "Project actions",
  services: "Services",
  projects: "Projects",
  service_pages: "Service pages",
  project_pages: "Project pages",
  account: "Account",
};

export const COMMAND_GROUP_ORDER = Object.keys(
  COMMAND_GROUP_TITLES,
) as CommandGroupId[];

export type CommandBadge = Partial<
  Pick<TagProps, "colorPalette" | "variant">
> & {
  label: string;
};

type CommandBase = {
  id: string;
  group: CommandGroupId;
  icon: Icons;
  title: string;
  description?: string;
  keywords?: string[];
  badges?: Array<CommandBadge>;
  disabled?: boolean;
  /**
   * Keeps the command out of "Recently used". Use it for commands that must
   * not be one keystroke away on the next visit, such as signing out.
   */
  excludeFromRecents?: boolean;
};

export type NavigationCommandTarget = {
  to: NavigateOptions["to"];
  params?: NavigateOptions["params"];
  search?: NavigateOptions["search"];
};

export type NavigationCommand = CommandBase & {
  kind: "navigation";
  target: NavigationCommandTarget;
};

export type ActionCommand = CommandBase & {
  kind: "action";
  run: (context: CommandContext) => Promise<void> | void;
};

export type CollectionCommand = CommandBase & {
  kind: "collection";
  getChildren: () => ResolvedCommand[];
};

export type ResolvedCommand =
  | NavigationCommand
  | ActionCommand
  | CollectionCommand;

export type CommandGroups = {
  [id in CommandGroupId]: Array<ResolvedCommand>;
};

export type CommandContext = {
  notify: {
    success: (message: string) => void;
    error: (message: string) => void;
  };
  /**
   * Runs `focus` once the palette has closed. Closing returns focus to the
   * palette trigger, and while the palette is open the rest of the page is
   * inert, so an action that moves focus elsewhere must do it here.
   */
  focusAfterClose: (focus: () => void) => void;
};
